import { useCallback, useContext, useMemo } from 'react';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { getShowGroupChat, hasNotSeenDiscoveryFeature } from 'utils';
import { FOURTH_RELEASE_DISCOVERING_FEATURE } from 'data/discoveringFeature';

export default function useGroupChatBuySideDiscovery() {
  const { currentUser, updatePreferences } = useContext(CurrentUserContext);
  const { seenDataRoomGroupChatBuySideFeature, openedGroupChatDrawerBuySide } =
    currentUser || {};

  const { isBuySide, openChatDrawer } = useContext(DataRoomContext);
  const { buySideGroups } = useContext(MembersContext);

  const { activeChatSessionId, dataRoomMembers } = buySideGroups?.[0] || {};

  const showGroupChat = useMemo(
    () => getShowGroupChat(activeChatSessionId, dataRoomMembers),
    [activeChatSessionId, dataRoomMembers],
  );

  const showCard = useMemo(
    () =>
      isBuySide &&
      showGroupChat &&
      hasNotSeenDiscoveryFeature(
        seenDataRoomGroupChatBuySideFeature,
        FOURTH_RELEASE_DISCOVERING_FEATURE,
      ),
    [isBuySide, showGroupChat, seenDataRoomGroupChatBuySideFeature],
  );

  const onUnderstood = useCallback(() => {
    if (seenDataRoomGroupChatBuySideFeature) return;
    updatePreferences({ seenDataRoomGroupChatBuySideFeature: true });
  }, [seenDataRoomGroupChatBuySideFeature, updatePreferences]);

  const markDrawerOpened = useCallback(() => {
    if (openedGroupChatDrawerBuySide) return;
    updatePreferences({ openedGroupChatDrawerBuySide: true });
  }, [openedGroupChatDrawerBuySide, updatePreferences]);

  const onOpenGroupChat = useCallback(() => {
    onUnderstood();
    markDrawerOpened();
    openChatDrawer(activeChatSessionId);
  }, [onUnderstood, markDrawerOpened, openChatDrawer, activeChatSessionId]);

  return {
    showCard,
    shouldHighlightGroupChat:
      hasNotSeenDiscoveryFeature(
        seenDataRoomGroupChatBuySideFeature,
        FOURTH_RELEASE_DISCOVERING_FEATURE,
      ) && !openedGroupChatDrawerBuySide,
    onUnderstood,
    onOpenGroupChat,
    markDrawerOpened,
  };
}
